/**
 * Shuffle PCS — Shopify PC Builder v3
 * Corsair-inspired layout | List rows | Right preview panel
 * Step-by-step | Variants | Summary page | VAT 16% Kenya
 */

(function () {
  'use strict';

  const VAT_RATE = 0.16;

  const CATEGORIES = [
    { id: 'cpu',         label: 'CPU',        short: 'CPU',     icon: '⚙️',  tag: 'pc-builder:cpu',         optional: false },
    { id: 'motherboard', label: 'Motherboard', short: 'M/Board', icon: '🔌',  tag: 'pc-builder:motherboard', optional: false },
    { id: 'ram',         label: 'RAM',         short: 'RAM',     icon: '📊',  tag: 'pc-builder:ram',         optional: false },
    { id: 'gpu',         label: 'GPU',         short: 'GPU',     icon: '🎮',  tag: 'pc-builder:gpu',         optional: true  },
    { id: 'storage',     label: 'Storage',     short: 'Storage', icon: '💾',  tag: 'pc-builder:storage',     optional: false },
    { id: 'psu',         label: 'PSU',         short: 'PSU',     icon: '⚡',  tag: 'pc-builder:psu',         optional: false },
    { id: 'cooler',      label: 'CPU Cooler',  short: 'Cooler',  icon: '❄️',  tag: 'pc-builder:cooler',      optional: true  },
    { id: 'case',        label: 'Case',        short: 'Case',    icon: '🖥️',  tag: 'pc-builder:case',        optional: false },
    { id: 'fans',        label: 'Case Fans',   short: 'Fans',    icon: '🌀',  tag: 'pc-builder:fans',        optional: true  },
    { id: 'accessories', label: 'Add-ons', short: 'Add-ons', icon: '⌨️',  tag: 'pc-builder:accessories', optional: true,  multi: true  },
  ];

  /* ── Helpers ──────────────────────────────────────────── */

  function fmt(n) {
    return 'KES ' + Number(n).toLocaleString('en-KE', {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
  }

  function getPrices(part) {
    if (!part) return { exVAT: 0, vat: 0, incVAT: 0 };
    const price = (part.selectedVariant ? part.selectedVariant.price : null) ?? part.price;
    if (part.taxIncluded) {
      const exVAT = price / (1 + VAT_RATE);
      return { exVAT, vat: price - exVAT, incVAT: price };
    }
    return { exVAT: price, vat: price * VAT_RATE, incVAT: price * (1 + VAT_RATE) };
  }

  function esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* ── PCBuilder ────────────────────────────────────────── */

  class PCBuilder {
    constructor(container) {
      this.container    = container;
      this.token        = container.dataset.token;
      this.storeDomain  = container.dataset.store;

      this.products      = {};
      this.selectedParts = {};
      this.currentStep   = 0;
      this.budget        = '';
      this.search        = '';
      this.typeFilters   = [];
      this.customer      = { name: '', email: '', phone: '' };
      this.showSummary   = false;

      this.container.innerHTML = '<div class="spb-loading"><div class="spb-spinner"></div><p>Loading products…</p></div>';
      this._loadJsPDF();
      this._loadProducts();
    }

    _loadJsPDF() {
      if (window.jspdf || document.querySelector('script[src*="jspdf"]')) return;
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      document.head.appendChild(s);
    }

    async _loadProducts() {
      try {
        for (const cat of CATEGORIES) {
          const query = `{
            products(first: 50, query: "tag:'${cat.tag}'") {
              edges {
                node {
                  id title tags productType
                  variants(first: 20) {
                    edges { node { id title price { amount } availableForSale selectedOptions { name value } image { url } } }
                  }
                  images(first: 1) { edges { node { url } } }
                }
              }
            }
          }`;

          const res = await fetch(`https://${this.storeDomain}/api/2024-01/graphql.json`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': this.token,
            },
            body: JSON.stringify({ query }),
          });

          if (!res.ok) throw new Error(`API error ${res.status} — check your Storefront Access Token.`);
          const data = await res.json();
          if (data.errors) throw new Error(data.errors[0].message);

          this.products[cat.id] = data.data.products.edges.map(e => {
            const variants = e.node.variants.edges.map(v => ({
              id: v.node.id, title: v.node.title,
              price: parseFloat(v.node.price.amount || 0),
              available: v.node.availableForSale,
              options: v.node.selectedOptions,
              image: v.node.image?.url || null,
            }));
            const hasRealVariants = variants.length > 1 ||
              (variants.length === 1 && variants[0].title !== 'Default Title');
            return {
              id: e.node.id, title: e.node.title, tags: e.node.tags || [],
              productType: e.node.productType || '',
              price: parseFloat(e.node.variants.edges[0]?.node.price.amount || 0),
              variants, hasVariants: hasRealVariants,
              selectedVariant: hasRealVariants ? null : (variants[0] || null),
              available: e.node.variants.edges[0]?.node.availableForSale ?? true,
              image: e.node.images.edges[0]?.node.url || null,
              taxIncluded: (e.node.tags || []).includes('tax-included'),
            };
          });
        }
        this._render();
      } catch (e) {
        this.container.innerHTML = `<div class="spb-error"><p>⚠ ${esc(e.message)}</p></div>`;
      }
    }

    _getCompatibility() {
      const errors = [], warnings = [];
      const cpu = this.selectedParts.cpu, mb = this.selectedParts.motherboard, ram = this.selectedParts.ram;
      if (cpu && mb) {
        const cs = cpu.tags.find(t => t.startsWith('socket:'));
        const ms = mb.tags.find(t => t.startsWith('socket:'));
        if (cs && ms && cs !== ms)
          errors.push(`Socket mismatch: CPU (${cs.replace('socket:','').toUpperCase()}) ≠ Motherboard (${ms.replace('socket:','').toUpperCase()})`);
      }
      if (mb && ram) {
        const md = mb.tags.find(t => t.startsWith('ddr:'));
        const rd = ram.tags.find(t => t.startsWith('ddr:'));
        if (md && rd && md !== rd)
          errors.push(`RAM mismatch: Motherboard needs ${md.replace('ddr:','').toUpperCase()}, selected ${rd.replace('ddr:','').toUpperCase()}`);
      }
      if (!this.selectedParts.cpu)  warnings.push('No CPU selected');
      if (!this.selectedParts.psu)  warnings.push('No PSU selected');
      if (!this.selectedParts.case) warnings.push('No Case selected');
      return { errors, warnings };
    }

    _getTotals() {
      let exVAT = 0, vat = 0;
      CATEGORIES.forEach(cat => {
        const val = this.selectedParts[cat.id];
        if (!val) return;
        if (cat.multi) {
          val.forEach(p => { const pr = getPrices(p); exVAT += pr.exVAT; vat += pr.vat; });
        } else {
          const pr = getPrices(val); exVAT += pr.exVAT; vat += pr.vat;
        }
      });
      return { exVAT, vat, incVAT: exVAT + vat };
    }

    _getFiltered(catId) {
      const budgetN = parseFloat(this.budget) || 0;
      return (this.products[catId] || []).filter(p => {
        const { incVAT } = getPrices(p);
        return (!budgetN || incVAT <= budgetN) &&
               (!this.search || p.title.toLowerCase().includes(this.search.toLowerCase())) &&
               (this.typeFilters.length === 0 || this.typeFilters.includes(p.productType));
      });
    }

    _render() {
      this.showSummary ? this._renderSummary() : this._renderStep();
    }

    /* ── Step page ──────────────────────────────────────── */

    _renderStep() {
      const cat     = CATEGORIES[this.currentStep];
      const parts   = this._getFiltered(cat.id);
      const sel     = cat.multi ? (this.selectedParts[cat.id] || []) : this.selectedParts[cat.id];
      const hasSel  = cat.multi ? sel.length > 0 : !!sel;
      const { errors } = this._getCompatibility();
      const budgetN = parseFloat(this.budget) || 0;
      const { incVAT } = this._getTotals();
      const over    = budgetN > 0 && incVAT > budgetN;
      const isFirst = this.currentStep === 0;

      this.container.innerHTML = `
        <!-- Header -->
        <div class="spb-header">
          <div>
            <div class="spb-logo-name">PC BUILDER</div>
          </div>
          <div class="spb-header-controls">
            <div class="spb-budget-wrap">
              <label class="spb-label" for="spb-budget">Budget (Inc-VAT)</label>
              <input type="number" id="spb-budget" class="spb-input spb-budget-input${over?' over':''}"
                placeholder="KES 0" value="${esc(this.budget)}">
            </div>
          </div>
        </div>

        <!-- Progress bar -->
        ${this._renderProgressBar()}

        <!-- Filter bar -->
        <div class="spb-filter-bar" style="margin-top:16px;margin-bottom:20px">
          <input type="text" id="spb-search" class="spb-input spb-search-input" style="padding-left:12px;padding-right:12px"
            placeholder="Search ${esc(cat.label)}…" value="${esc(this.search)}">
          ${(() => {
            const types = [...new Set((this.products[cat.id] || []).map(p => p.productType).filter(Boolean))];
            if (types.length < 2) return '';
            const activeCount = this.typeFilters.length;
            return `
              <div class="spb-type-dropdown" id="spb-type-dropdown">
                <button class="spb-type-dropdown-btn" id="spb-type-dropdown-btn" style="padding-left:12px;padding-right:12px">
                  Product Type${activeCount > 0 ? ` (${activeCount})` : ''} <span class="spb-dropdown-arrow">▾</span>
                </button>
                <div class="spb-type-dropdown-panel" id="spb-type-dropdown-panel">
                  <div class="spb-type-dropdown-header" style="padding:12px 20px">
                    <span>${activeCount} selected</span>
                    <button class="spb-type-reset" id="spb-type-reset">Reset</button>
                  </div>
                  ${types.map(t => {
                    const count = (this.products[cat.id] || []).filter(p => p.productType === t).length;
                    const checked = this.typeFilters.includes(t);
                    return `<label class="spb-type-option" style="padding:12px 20px">
                      <input type="checkbox" class="spb-type-check" value="${esc(t)}" ${checked ? 'checked' : ''}>
                      ${esc(t)} (${count})
                    </label>`;
                  }).join('')}
                </div>
              </div>
            `;
          })()}
        </div>

        ${errors.length ? `<div class="spb-compat-errors">${errors.map(e=>`<div>⚠ ${esc(e)}</div>`).join('')}</div>` : ''}

        <!-- Two-column layout -->
        <div class="spb-step-layout" style="margin-top:16px;margin-bottom:16px">

          <!-- Product list -->
          <div>
            ${parts.length === 0
              ? `<div class="spb-empty">
                  <div class="spb-empty-icon">📦</div>
                  <p>No products found</p>
                  <small>Tag products with <code>pc-builder:${cat.id}</code> in Shopify</small>
                </div>`
              : `<div class="spb-product-list" id="spb-list" style="margin-bottom:20px">
                  ${parts.map(p => this._renderRow(p, cat)).join('')}
                </div>`
            }
          </div>

          <!-- Preview panel -->
          <div class="spb-preview-panel" style="background:#fff;border:1px solid rgba(101,142,156,0.25);position:sticky;top:20px;">
            <div class="spb-preview-header" style="background:#222222;padding:14px 16px;display:flex;align-items:center;justify-content:space-between;">
              <span class="spb-preview-header-title">Select ${esc(cat.label)}</span>
            </div>
            <div class="spb-preview-body" id="spb-preview-body" style="padding:20px 20px 24px;">
              ${cat.multi ? this._renderMultiPreview(sel, cat) : (hasSel ? this._renderPreview(sel) : this._renderPreviewEmpty(cat))}
            </div>
          </div>

        </div>

        <!-- Nav bar -->
        <div class="spb-nav-bar">
          <div class="spb-nav-left">
            ${!isFirst
              ? `<button class="spb-back-link" id="spb-back">← Back to ${esc(CATEGORIES[this.currentStep-1]?.label||'')}</button>`
              : '<div></div>'
            }
          </div>
          <div class="spb-nav-right">
            ${hasSel ? `<span class="spb-selected-confirm">✓ Selection made</span>` : ''}
            ${cat.optional && !hasSel
              ? `<button class="spb-btn spb-btn-secondary" id="spb-skip">Skip</button>`
              : ''
            }
            <button class="spb-btn spb-btn-primary" id="spb-next"
              ${!hasSel && !cat.optional ? 'disabled' : ''}>
              ${this.currentStep === CATEGORIES.length-1 ? 'Review Build' : 'Next'} →
            </button>
          </div>
        </div>
      `;

      this._bindStep();
    }

    _renderProgressBar() {
      return `
        <div class="spb-progress-bar">
          ${CATEGORIES.map((c, i) => {
            const isSelected = c.multi
              ? (this.selectedParts[c.id] || []).length > 0
              : !!this.selectedParts[c.id];
            const current    = i === this.currentStep;
            const done       = isSelected && !current;
            return `<button class="spb-progress-step${done ? ' done' : ''}${current ? ' current' : ''}"
              data-action="go-to-step-nav" data-step="${i}">
              <span class="spb-step-label">${done ? '✓ ' : ''}${esc(c.label)}</span>
              <span class="spb-step-short">${done ? '✓ ' : ''}${esc(c.short)}</span>
            </button>`;
          }).join('')}
        </div>
      `;
    }

    _renderRow(part, cat) {
      const selInstance = cat.multi
        ? (this.selectedParts[cat.id] || []).find(s => s.id === part.id)
        : this.selectedParts[cat.id];
      const isSel      = cat.multi ? !!selInstance : this.selectedParts[cat.id]?.id === part.id;
      const displayPart = isSel ? selInstance : part;
      const { exVAT, vat, incVAT } = getPrices(displayPart);
      const compatTags = part.tags.filter(t => t.startsWith('socket:') || t.startsWith('ddr:'));
      const selVariant = isSel ? selInstance?.selectedVariant : null;

      return `
        <button class="spb-product-row${isSel?' selected':''}${!part.available?' unavailable':''}" style="padding:20px 15px 20px 0px"
          data-action="select-part" data-part-id="${esc(part.id)}"
          ${!part.available ? 'disabled' : ''}>
          <div class="spb-row-img">
            ${part.image
              ? `<img src="${esc(part.image)}" alt="${esc(part.title)}" loading="lazy">`
              : `<span>${cat.icon}</span>`
            }
          </div>
          <div class="spb-row-body">
            <div class="spb-row-name" style="font-size:14px;font-weight:400;margin-bottom:8px">${esc(part.title)}</div>
            <div class="spb-row-price-line" style="margin-bottom:8px">
              <span class="spb-row-inc-price">${fmt(exVAT)}</span>
              ${!part.available ? '<span class="spb-badge spb-badge-oos">Out of stock</span>' : ''}
            </div>
            ${isSel && part.hasVariants ? `
              <div class="spb-variant-wrap" onclick="event.stopPropagation()">
                <div class="spb-variant-label" style="margin-bottom:8px; margin-top:12px">Select option</div>
                <select class="spb-variant-select" data-action="select-variant" data-part-id="${esc(part.id)}" style="padding:3px;min-width:300px">
                  <option value="">— Choose option —</option>
                  ${part.variants.map(v => `
                    <option value="${esc(v.id)}"
                      ${!v.available?'disabled':''}
                      ${selVariant?.id===v.id?'selected':''}>
                      ${esc(v.title)} — ${fmt(part.taxIncluded ? v.price/(1+VAT_RATE) : v.price)}
                    </option>
                  `).join('')}
                </select>
              </div>
            ` : ''}
          </div>
          <div class="spb-row-right">
            ${isSel ? '<div class="spb-row-check">✕</div>' : ''}
            <div class="spb-row-badges">
              ${compatTags.map(t=>`<span class="spb-badge spb-badge-tag">${esc(t.replace(/socket:|ddr:/g,'').toUpperCase())}</span>`).join('')}
            </div>
          </div>
        </button>
      `;
    }

    _renderPreviewEmpty(cat) {
      return `
        <div class="spb-preview-empty">
          <div class="spb-preview-empty-icon">${cat.icon}</div>
          <p>Select a ${esc(cat.label)} from the list to see details here.</p>
        </div>
      `;
    }

    _renderMultiPreview(items, cat) {
      if (!items || items.length === 0) return this._renderPreviewEmpty(cat);
      const total = items.reduce((s, p) => s + getPrices(p).exVAT, 0);
      return `
        <div class="spb-preview-empty">
          <div class="spb-preview-empty-icon">${cat.icon}</div>
          <p style="font-weight:700;color:var(--black);margin-bottom:12px">${items.length} item${items.length !== 1 ? 's' : ''} selected</p>
          ${items.map(p => `
            <div style="font-size:12px;color:var(--text-mid);margin-bottom:6px;text-align:left;width:100%">
              ${esc(p.title)} — ${fmt(getPrices(p).exVAT)}
            </div>`).join('')}
          <div style="margin-top:12px;font-weight:700;color:var(--black);border-top:1px solid var(--border);padding-top:10px;width:100%;text-align:left">
            Total: ${fmt(total)}
          </div>
        </div>
      `;
    }

    _renderPreview(part) {
      const { exVAT, vat, incVAT } = getPrices(part);
      const selVariant = part.selectedVariant && part.hasVariants ? part.selectedVariant : null;
      return `
        <div class="spb-preview-img">
          ${(part.selectedVariant?.image || part.image)
            ? `<img src="${esc(part.selectedVariant?.image || part.image)}" alt="${esc(part.title)}">`
            : `<span>${CATEGORIES.find(c=>this.selectedParts[c.id]?.id===part.id)?.icon||'📦'}</span>`
          }
        </div>
        <div class="spb-preview-name" style="margin-top:20px;margin-bottom:15px">${esc(part.title)}</div>
        ${selVariant ? `<div class="spb-preview-variant">Option: ${esc(selVariant.title)}</div>` : ''}
        <div class="spb-preview-prices">
          <div class="spb-preview-price-inc">${fmt(exVAT)}</div>
          <div class="spb-preview-price-sub">
            <span>Incl-VAT: ${fmt(incVAT)}</span>
            <span>VAT: ${fmt(vat)}</span>
          </div>
        </div>
      `;
    }

    _bindStep() {
      const cat = CATEGORIES[this.currentStep];

      // Product row clicks
      this.container.querySelectorAll('[data-action="select-part"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const partId = btn.dataset.partId;
          const part   = (this.products[cat.id] || []).find(p => p.id === partId);
          if (!part || !part.available) return;

          if (cat.multi) {
            const arr = this.selectedParts[cat.id] || [];
            if (arr.find(s => s.id === partId)) {
              const updated = arr.filter(s => s.id !== partId);
              if (updated.length) this.selectedParts[cat.id] = updated;
              else delete this.selectedParts[cat.id];
            } else {
              this.selectedParts[cat.id] = [...arr, {...part, selectedVariant: part.hasVariants ? null : (part.variants[0] || null)}];
            }
          } else {
            if (this.selectedParts[cat.id]?.id === partId) {
              delete this.selectedParts[cat.id];
            } else {
              this.selectedParts[cat.id] = {...part, selectedVariant: part.hasVariants ? null : (part.variants[0] || null)};
            }
          }
          this._render();
        });
      });

      // Variant selects
      this.container.querySelectorAll('[data-action="select-variant"]').forEach(sel => {
        sel.addEventListener('click', e => e.stopPropagation());
        sel.addEventListener('change', () => {
          const part    = this.selectedParts[cat.id];
          if (!part) return;
          const variant = part.variants.find(v => v.id === sel.value) || null;
          this.selectedParts[cat.id] = { ...part, selectedVariant: variant };
          // Update preview panel only
          const preview = this.container.querySelector('#spb-preview-body');
          if (preview) preview.innerHTML = this._renderPreview(this.selectedParts[cat.id]);
        });
      });

      // Progress bar step navigation
      this.container.querySelectorAll('[data-action="go-to-step-nav"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const step = parseInt(btn.dataset.step, 10);
          if (step !== this.currentStep) {
            this.currentStep = step;
            this.search = '';
            this.typeFilters = [];
            this._render();
          }
        });
      });

      // Back
      this.container.querySelector('#spb-back')?.addEventListener('click', () => {
        this.currentStep = Math.max(0, this.currentStep - 1);
        this.search = '';
        this.typeFilters = [];
        this._render();
      });

      // Next
      this.container.querySelector('#spb-next')?.addEventListener('click', () => {
        if (this.currentStep < CATEGORIES.length - 1) {
          this.currentStep++;
          this.search = '';
          this.typeFilters = [];
        } else {
          this.showSummary = true;
        }
        this._render();
      });

      // Skip
      this.container.querySelector('#spb-skip')?.addEventListener('click', () => {
        if (this.currentStep < CATEGORIES.length - 1) {
          this.currentStep++;
          this.search = '';
          this.typeFilters = [];
          this._render();
        } else {
          this.showSummary = true;
          this._render();
        }
      });

      // Type filter dropdown
      const dropdownBtn   = this.container.querySelector('#spb-type-dropdown-btn');
      const dropdownPanel = this.container.querySelector('#spb-type-dropdown-panel');
      if (dropdownBtn && dropdownPanel) {
        dropdownBtn.addEventListener('click', e => {
          e.stopPropagation();
          dropdownPanel.classList.toggle('open');
        });
        document.addEventListener('click', () => dropdownPanel.classList.remove('open'), { once: true });
      }

      this.container.querySelectorAll('.spb-type-check').forEach(cb => {
        cb.addEventListener('change', () => {
          this.typeFilters = [...this.container.querySelectorAll('.spb-type-check:checked')].map(c => c.value);
          const list  = this.container.querySelector('#spb-list');
          if (!list) return;
          const parts = this._getFiltered(cat.id);
          list.innerHTML = parts.length === 0
            ? '<div class="spb-empty"><div class="spb-empty-icon">📦</div><p>No products found</p></div>'
            : parts.map(p => this._renderRow(p, cat)).join('');
          // update selected count labels
          const activeCount = this.typeFilters.length;
          const btn = this.container.querySelector('#spb-type-dropdown-btn');
          if (btn) btn.innerHTML = `Product Type${activeCount > 0 ? ` (${activeCount})` : ''} <span class="spb-dropdown-arrow">▾</span>`;
          const hdr = this.container.querySelector('#spb-type-dropdown-panel .spb-type-dropdown-header span');
          if (hdr) hdr.textContent = `${activeCount} selected`;
          this._bindRowClicks(cat);
        });
      });

      this.container.querySelector('#spb-type-reset')?.addEventListener('click', e => {
        e.stopPropagation();
        this.typeFilters = [];
        this._render();
      });

      // Clear
      this.container.querySelector('#spb-clear-cat')?.addEventListener('click', () => {
        delete this.selectedParts[cat.id];
        this._render();
      });

      // Search
      this.container.querySelector('#spb-search')?.addEventListener('input', e => {
        this.search = e.target.value;
        const list  = this.container.querySelector('#spb-list');
        if (!list) return;
        const parts = this._getFiltered(cat.id);
        list.innerHTML = parts.length === 0
          ? '<div class="spb-empty"><div class="spb-empty-icon">📦</div><p>No products found</p></div>'
          : parts.map(p => this._renderRow(p, cat)).join('');
        this._bindRowClicks(cat);
      });

      // Budget
      this.container.querySelector('#spb-budget')?.addEventListener('input', e => {
        this.budget = e.target.value;
        e.target.classList.toggle('over', parseFloat(e.target.value)>0 && this._getTotals().incVAT>parseFloat(e.target.value));
        const list  = this.container.querySelector('#spb-list');
        if (!list) return;
        const parts = this._getFiltered(cat.id);
        list.innerHTML = parts.length===0
          ? '<div class="spb-empty"><div class="spb-empty-icon">📦</div><p>No products within budget</p></div>'
          : parts.map(p=>this._renderRow(p,cat)).join('');
        this._bindRowClicks(cat);
      });
    }

    _bindRowClicks(cat) {
      const list = this.container.querySelector('#spb-list');
      if (!list) return;
      list.querySelectorAll('[data-action="select-part"]').forEach(btn => {
        btn.addEventListener('click', () => {
          const part = (this.products[cat.id]||[]).find(p=>p.id===btn.dataset.partId);
          if (!part||!part.available) return;
          if (cat.multi) {
            const arr = this.selectedParts[cat.id] || [];
            if (arr.find(s=>s.id===btn.dataset.partId)) {
              const updated = arr.filter(s=>s.id!==btn.dataset.partId);
              if (updated.length) this.selectedParts[cat.id] = updated;
              else delete this.selectedParts[cat.id];
            } else {
              this.selectedParts[cat.id] = [...arr, {...part, selectedVariant: part.hasVariants?null:(part.variants[0]||null)}];
            }
          } else {
            if (this.selectedParts[cat.id]?.id===btn.dataset.partId) {
              delete this.selectedParts[cat.id];
            } else {
              this.selectedParts[cat.id] = {...part, selectedVariant: part.hasVariants?null:(part.variants[0]||null)};
            }
          }
          this._render();
        });
      });
    }

    /* ── Summary page ───────────────────────────────────── */

    _renderSummary() {
      const { exVAT, vat, incVAT } = this._getTotals();
      const budgetN  = parseFloat(this.budget) || 0;
      const over     = budgetN > 0 && incVAT > budgetN;
      const { errors, warnings } = this._getCompatibility();
      const selCount = CATEGORIES.reduce((n, cat) => {
        const val = this.selectedParts[cat.id];
        if (!val) return n;
        return n + (cat.multi ? (val.length > 0 ? 1 : 0) : 1);
      }, 0);

      this.container.innerHTML = `
        <style>
          @media (max-width: 600px) {
            .spb-ov-header { grid-template-columns: 1fr 100px 60px !important; gap:8px !important; }
            .spb-ov-row    { grid-template-columns: 1fr 100px 60px !important; gap:8px !important; }
            .spb-ov-thumb, .spb-ov-cat,
            .spb-ov-header-thumb, .spb-ov-header-cat { display:none !important; }
          }
        </style>
        <div class="spb-header">
          <div><div class="spb-logo-name">PC BUILDER</div></div>
          <div class="spb-header-controls"></div>
        </div>

        <!-- Overview bar -->
        <div class="spb-overview-bar">
          <span class="spb-overview-bar-title">Overview</span>
          <button class="spb-btn spb-btn-primary spb-overview-edit" id="spb-back-to-build">← Edit Build</button>
        </div>

        <div style="display:flex;flex-direction:column;gap:24px;">

          ${errors.length ? `<div style="margin-bottom:14px;padding:10px 14px;background:#fef2f2;border-left:4px solid #dc2626;display:flex;flex-direction:column;gap:3px;">${errors.map(e=>`<div style="color:#dc2626;font-size:13px;font-weight:600;">⚠ ${esc(e)}</div>`).join('')}</div>` : ''}
          ${warnings.length ? `<div>${warnings.map(w=>`<div style="color:#b45309;font-size:13px;font-weight:600;margin-bottom:4px;">⚠ ${esc(w)}</div>`).join('')}</div>` : ''}

          <!-- Customer -->
          <div style="background:#f7f7f7;border:1px solid #e0e0e0;padding:20px;">
            <h3 style="font-size:11px;font-weight:800;color:#777;text-transform:uppercase;letter-spacing:1px;margin-bottom:14px;">Customer Details (optional)</h3>
            <div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:12px;">
              <div style="display:flex;flex-direction:column;gap:5px;">
                <label style="font-size:11px;font-weight:700;color:#777;text-transform:uppercase;letter-spacing:0.5px;">Customer Name</label>
                <input class="spb-input" id="spb-cust-name" style="width:100%;background:#fff;padding:5px;" placeholder="e.g. Awesome Dude" value="${esc(this.customer.name)}">
              </div>
              <div style="display:flex;flex-direction:column;gap:5px;">
                <label style="font-size:11px;font-weight:700;color:#777;text-transform:uppercase;letter-spacing:0.5px;">Email Address</label>
                <input class="spb-input" id="spb-cust-email" style="width:100%;background:#fff; padding:5px;" placeholder="e.g. john@email.com" value="${esc(this.customer.email)}">
              </div>
              <div style="display:flex;flex-direction:column;gap:5px;">
                <label style="font-size:11px;font-weight:700;color:#777;text-transform:uppercase;letter-spacing:0.5px;">Phone Number</label>
                <input class="spb-input" id="spb-cust-phone" style="width:100%;background:#fff;padding:5px;" placeholder="e.g. 0756 509 737" value="${esc(this.customer.phone)}">
              </div>
            </div>
          </div>

          <!-- Parts table -->
          <div style="border:1px solid #e0e0e0;">
            <div class="spb-ov-header" style="display:grid;grid-template-columns:50px 130px 1fr 150px 90px;background:#efefef;padding:10px 16px;gap:12px;font-size:10px;font-weight:800;color:#777;text-transform:uppercase;letter-spacing:0.8px;border-bottom:1px solid #e0e0e0;">
              <div class="spb-ov-header-thumb"></div>
              <div class="spb-ov-header-cat">Category</div>
              <div>Component</div>
              <div style="text-align:right">Ex-VAT</div>
              <div style="text-align:center">Action</div>
            </div>
            ${CATEGORIES.map((cat,idx) => {
              const val = this.selectedParts[cat.id];
              if (!val || (cat.multi && val.length === 0)) return `
                <div class="spb-ov-row" style="display:grid;grid-template-columns:50px 130px 1fr 150px 90px;padding:12px 16px;gap:12px;align-items:center;border-bottom:1px solid #e0e0e0;font-size:13px;color:#aaa;">
                  <div class="spb-ov-thumb" style="width:40px;height:36px;display:flex;align-items:center;justify-content:center;background:#f7f7f7;border:1px solid #e0e0e0;font-size:16px;flex-shrink:0;">${cat.icon}</div>
                  <div class="spb-ov-cat" style="font-size:11px;font-weight:700;color:#777;text-transform:uppercase;letter-spacing:0.5px;">${esc(cat.label)}</div>
                  <div><span style="font-style:italic;color:#aaa;font-size:13px;">Not selected</span></div>
                  <div></div>
                  <div style="display:flex;justify-content:flex-end;">
                    <button style="background:none;border:1px solid #c8c8c8;padding:4px 10px;font-size:11px;font-family:'Assistant',sans-serif;font-weight:700;color:#777;cursor:pointer;text-transform:uppercase;letter-spacing:0.3px;" data-action="go-to-step" data-step="${idx}">Add</button>
                  </div>
                </div>`;
              if (cat.multi) {
                return val.map((p, i) => {
                  const pr = getPrices(p);
                  return `
                    <div class="spb-ov-row" style="display:grid;grid-template-columns:50px 130px 1fr 150px 90px;padding:12px 16px;gap:12px;align-items:center;border-bottom:1px solid #e0e0e0;font-size:13px;">
                      <div class="spb-ov-thumb" style="width:40px;height:36px;display:flex;align-items:center;justify-content:center;background:#f7f7f7;border:1px solid #e0e0e0;font-size:16px;flex-shrink:0;">${p.image?`<img src="${esc(p.image)}" alt="" style="width:100%;height:100%;object-fit:contain;">`:cat.icon}</div>
                      <div class="spb-ov-cat" style="font-size:11px;font-weight:700;color:#777;text-transform:uppercase;letter-spacing:0.5px;">${i === 0 ? esc(cat.label) : ''}</div>
                      <div>
                        <div style="font-weight:600;color:#111;font-size:13px;word-break:break-word;">${esc(p.title)}</div>
                        ${p.selectedVariant&&p.hasVariants?`<div style="font-size:11px;color:#777;margin-top:1px;">${esc(p.selectedVariant.title)}</div>`:''}
                      </div>
                      <div style="font-weight:500;color:#444;text-align:right;font-size:12px;">${fmt(pr.exVAT)}</div>
                      <div style="display:flex;justify-content:flex-end;">
                        <button style="background:none;border:1px solid #c8c8c8;padding:4px 10px;font-size:11px;font-family:'Assistant',sans-serif;font-weight:700;color:#777;cursor:pointer;text-transform:uppercase;letter-spacing:0.3px;" data-action="go-to-step" data-step="${idx}">Edit</button>
                      </div>
                    </div>`;
                }).join('');
              }
              const pr = getPrices(val);
              return `
                <div class="spb-ov-row" style="display:grid;grid-template-columns:50px 130px 1fr 150px 90px;padding:12px 16px;gap:12px;align-items:center;border-bottom:1px solid #e0e0e0;font-size:13px;">
                  <div class="spb-ov-thumb" style="width:40px;height:36px;display:flex;align-items:center;justify-content:center;background:#f7f7f7;border:1px solid #e0e0e0;font-size:16px;flex-shrink:0;">${val.image?`<img src="${esc(val.image)}" alt="" style="width:100%;height:100%;object-fit:contain;">`:cat.icon}</div>
                  <div class="spb-ov-cat" style="font-size:11px;font-weight:700;color:#777;text-transform:uppercase;letter-spacing:0.5px;">${esc(cat.label)}</div>
                  <div>
                    <div style="font-weight:600;color:#111;font-size:13px;word-break:break-word;">${esc(val.title)}</div>
                    ${val.selectedVariant&&val.hasVariants?`<div style="font-size:11px;color:#777;margin-top:1px;">${esc(val.selectedVariant.title)}</div>`:''}
                  </div>
                  <div style="font-weight:500;color:#444;text-align:right;font-size:12px;">${fmt(pr.exVAT)}</div>
                  <div style="display:flex;justify-content:flex-end;">
                    <button style="background:none;border:1px solid #c8c8c8;padding:4px 10px;font-size:11px;font-family:'Assistant',sans-serif;font-weight:700;color:#777;cursor:pointer;text-transform:uppercase;letter-spacing:0.3px;" data-action="go-to-step" data-step="${idx}">Edit</button>
                  </div>
                </div>`;
            }).join('')}
          </div>

          <!-- Totals -->
          <div style="display:flex;justify-content:flex-end;">
            <div style="background:#f7f7f7;border:1px solid #e0e0e0;padding:20px 24px;min-width:300px;width:100%;max-width:400px;display:flex;flex-direction:column;gap:8px;">
              <div style="display:flex;justify-content:space-between;font-size:14px;gap:24px;">
                <span style="color:#777;font-weight:500;">Subtotal (Ex-VAT)</span>
                <span style="font-weight:600;color:#111;">${fmt(exVAT)}</span>
              </div>
              <div style="display:flex;justify-content:space-between;font-size:14px;gap:24px;">
                <span style="color:#777;font-weight:500;">VAT @ 16%</span>
                <span style="font-weight:600;color:#111;">${fmt(vat)}</span>
              </div>
              <hr style="border:none;border-top:1px solid #c8c8c8;margin:4px 0;">
              <div style="display:flex;justify-content:space-between;gap:24px;">
                <span style="font-size:16px;font-weight:800;color:#111;text-transform:uppercase;">Total</span>
                <span style="font-size:20px;font-weight:800;color:#111;">${fmt(incVAT)}</span>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div style="display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px;padding-top:8px;">
            <button class="spb-btn spb-btn-secondary" id="spb-clear-all">Clear Entire Build</button>
            <button class="spb-btn spb-btn-success" id="spb-pdf-btn" ${selCount===0?'disabled':''}>
              Generate Quote PDF
            </button>
          </div>

        </div>
      `;

      this._bindSummary();
    }

    _bindSummary() {
      this.container.querySelector('#spb-back-to-build')?.addEventListener('click', () => {
        this.showSummary = false;
        this.currentStep = CATEGORIES.length - 1;
        this._render();
      });

      // Progress bar navigation from summary
      this.container.querySelectorAll('[data-action="go-to-step-summary"]').forEach(btn => {
        btn.addEventListener('click', () => {
          this.showSummary = false;
          this.currentStep = parseInt(btn.dataset.step, 10);
          this.search = '';
          this._render();
        });
      });

      this.container.addEventListener('click', e => {
        const btn = e.target.closest('[data-action="go-to-step"]');
        if (btn) {
          this.showSummary = false;
          this.currentStep = parseInt(btn.dataset.step, 10);
          this.search = '';
          this._render();
        }
      });

      ['name','email','phone'].forEach(f => {
        this.container.querySelector(`#spb-cust-${f}`)?.addEventListener('input', e => {
          this.customer[f] = e.target.value;
        });
      });

      this.container.querySelector('#spb-budget-sum')?.addEventListener('input', e => {
        this.budget = e.target.value;
        this._render();
      });

      this.container.querySelector('#spb-clear-all')?.addEventListener('click', () => {
        if (!confirm('Clear the entire build and start over?')) return;
        this.selectedParts = {};
        this.showSummary   = false;
        this.currentStep   = 0;
        this._render();
      });

      this.container.querySelector('#spb-pdf-btn')?.addEventListener('click', () => {
        this._generatePDF();
      });
    }

    /* ── PDF ────────────────────────────────────────────── */

    _generatePDF() {
      if (!window.jspdf) { alert('PDF library is still loading. Please try again.'); return; }
      const jsPDF = window.jspdf.jsPDF;
      const doc   = new jsPDF();
      const { exVAT, vat, incVAT } = this._getTotals();
      const qn = `SPC-${Date.now().toString().slice(-6)}`;
      const dt = new Date().toLocaleDateString('en-KE',{day:'2-digit',month:'long',year:'numeric'});
      const c  = this.customer;

      // Header
      doc.setFillColor(10,10,20); doc.rect(0,0,210,40,'F');
      doc.setFillColor(245,200,0); doc.rect(0,37,210,3,'F');
      doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(18);
      doc.text('SHUFFLE PCS',14,16);
      doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(150,163,184);
      doc.text('Custom PC Build Quote',14,24); doc.text('Nairobi, Kenya',14,31);
      doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(10);
      doc.text(`Quote #${qn}`,138,16);
      doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(150,163,184);
      doc.text(dt,138,24); doc.text('Valid 7 days',138,31);

      // Customer
      let y=52;
      if(c.name||c.email||c.phone){
        doc.setTextColor(37,99,235); doc.setFontSize(8); doc.setFont('helvetica','bold');
        doc.text('PREPARED FOR',14,y); y+=5;
        if(c.name){doc.setTextColor(20,20,20);doc.setFontSize(11);doc.setFont('helvetica','bold');doc.text(c.name,14,y);y+=6;}
        doc.setFont('helvetica','normal');doc.setFontSize(9);doc.setTextColor(80,80,80);
        if(c.email){doc.text(c.email,14,y);y+=5;}
        if(c.phone){doc.text(c.phone,14,y);y+=5;}
        y+=4;
      }

      // Table header — 3 columns: Category | Component | Ex-VAT
      doc.setFillColor(10,15,30); doc.rect(14,y,182,9,'F');
      doc.setTextColor(255,255,255); doc.setFont('helvetica','bold'); doc.setFontSize(8);
      doc.text('CATEGORY',16,y+6);
      doc.text('COMPONENT',60,y+6);
      doc.text('EX-VAT',196,y+6,{align:'right'});
      y+=9;

      const colCat=16, colComp=60, colPrice=196, compWidth=110;

      let ri=0;
      CATEGORIES.forEach(cat=>{
        const val=this.selectedParts[cat.id]; if(!val) return;
        const items = cat.multi ? val : [val];
        items.forEach(p => {
          const pr=getPrices(p);
          const variant = p.selectedVariant && p.selectedVariant.title !== 'Default Title' ? p.selectedVariant.title : '';
          doc.setFont('helvetica','normal'); doc.setFontSize(8);
          const titleLines = doc.splitTextToSize(p.title, compWidth);
          const variantLines = variant ? doc.splitTextToSize(variant, compWidth) : [];
          const rowH = Math.max(8, (titleLines.length + variantLines.length) * 5 + 4);
          doc.setFillColor(...(ri%2===0?[248,250,252]:[255,255,255]));
          doc.rect(14,y,182,rowH,'F');
          doc.setTextColor(100,116,139); doc.setFont('helvetica','bold'); doc.setFontSize(8);
          doc.text(cat.label, colCat, y+5.5);
          doc.setTextColor(15,23,42); doc.setFont('helvetica','normal'); doc.setFontSize(8);
          doc.text(titleLines, colComp, y+5.5);
          if(variantLines.length){
            doc.setTextColor(100,116,139); doc.setFontSize(7.5);
            doc.text(variantLines, colComp, y+5.5+(titleLines.length*5));
          }
          doc.setTextColor(15,23,42); doc.setFont('helvetica','bold'); doc.setFontSize(8);
          doc.text(`KES ${pr.exVAT.toLocaleString('en-KE',{minimumFractionDigits:2,maximumFractionDigits:2})}`, colPrice, y+5.5, {align:'right'});
          y+=rowH; ri++;
        });
      });

      y+=6; doc.setDrawColor(220,230,240); doc.line(104,y,196,y); y+=6;

      const drawRow=(lbl,val,bold,hi)=>{
        if(hi){doc.setFillColor(245,200,0);doc.rect(104,y-5,92,10,'F');doc.setTextColor(0,0,0);}
        else{doc.setFillColor(245,247,250);doc.rect(104,y-5,92,9,'F');doc.setTextColor(80,90,100);}
        doc.setFont('helvetica',bold?'bold':'normal'); doc.setFontSize(9);
        doc.text(lbl,107,y+1.5); doc.text(val,193,y+1.5,{align:'right'});
        y+=hi?13:10;
      };
      drawRow('Subtotal (Excl. VAT)',`KES ${exVAT.toFixed(2)}`);
      drawRow('VAT @ 16%',`KES ${vat.toFixed(2)}`);
      drawRow('TOTAL (Incl. VAT)',`KES ${incVAT.toFixed(2)}`,true,true);

      doc.setTextColor(170,180,190); doc.setFont('helvetica','normal'); doc.setFontSize(7.5);
      doc.text('This is a quotation only. Prices subject to availability and may change without notice.',14,280);
      doc.text('Shuffle PCS | Nairobi, Kenya | shufflepcs.co.ke',14,286);

      doc.save(`ShufflePCS-Quote-${qn}.pdf`);
    }
  }

  /* ── Init ─────────────────────────────────────────────── */

  function init() {
    const container = document.getElementById('shuffle-pc-builder');
    if (!container) return;
    if (!container.dataset.token) {
      container.innerHTML = `<div class="spb-error">
        <p>⚠ No Storefront Access Token found.</p>
        <p style="font-size:13px;margin-top:8px;opacity:0.7">Go to Themes → Customize → PC Builder section → add your token.</p>
      </div>`;
      return;
    }
    new PCBuilder(container);
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

})();